import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, AlertTriangle } from 'lucide-react';
import { useCreateEquipment } from '@/hooks/useEquipment';
import {
    type Equipment,
    EQUIPMENT_CATEGORIES
} from '@/types/equipment';
import { useAuth } from '@/lib/auth';
import { useOrgId } from '@/hooks/useOrgContext';
import PtLogo from '@/components/PtLogo';
import FieldGroup from '@/components/equipment/FieldGroup';
import FieldRow from '@/components/equipment/FieldRow';
import {
    TextInput,
    TextareaInput,
    NumberInput,
    DateInput,
    SelectInput,
    CheckboxInput
} from '@/components/equipment/FieldEditors';

// ============================================================================
// EquipmentCreateRoute
// ----------------------------------------------------------------------------
// New equipment registration. Reuses the same FieldGroup/FieldRow/FieldEditors
// from the detail page. No view/edit toggle — the whole page is a form.
//
// Validation: moderate rigor — require name + category + at least one of
// (manufacturer, model_number) so items can be uniquely identified. Everything
// else is optional.
//
// Defaults: status='available', quantity_on_hand=1, minimum_quantity=1,
// reusable=true, is_active=true. Org_id auto-filled from the user's session.
//
// Auto-compute: if requires_maintenance + interval provided, sets initial
// next_maintenance_due = acquisition_date + interval.
// ============================================================================

type DraftEquipment = Partial<Equipment>;

export default function EquipmentCreateRoute() {
    const navigate = useNavigate();
    const { signOut, user } = useAuth();
    const org_id = useOrgId(); // Get org_id from context
    const createMut = useCreateEquipment();

    const [draft, setDraft] = useState<DraftEquipment>({
        status: 'available',
        quantity_on_hand: 1,
        quantity_in_use: 0,
        quantity_in_sterilization: 0,
        minimum_quantity: 1,
        reusable: true,
        is_active: true
    });

    const [validationError, setValidationError] = useState<string | null>(null);

    const setField = <K extends keyof Equipment>(
        key: K,
        value: Equipment[K]
    ) => {
        setDraft((prev) => ({ ...prev, [key]: value }));
        setValidationError(null);
    };

    async function handleSave() {
        setValidationError(null);

        // Required: name + category
        if (!draft.equipment_name?.trim()) {
            setValidationError('Equipment name is required.');
            return;
        }
        if (!draft.equipment_category?.trim()) {
            setValidationError('Equipment category is required.');
            return;
        }

        // Moderate validation: at least one of manufacturer or model_number
        if (!draft.manufacturer?.trim() && !draft.model_number?.trim()) {
            setValidationError(
                'Either manufacturer or model number is required to uniquely identify the item.'
            );
            return;
        }

        // Optional auto-compute: next_maintenance_due from acquisition + interval
        const patch: DraftEquipment = { ...draft };
        if (
            patch.requires_maintenance &&
            patch.maintenance_interval_days &&
            patch.acquisition_date
        ) {
            const acq = new Date(patch.acquisition_date);
            const next = new Date(acq);
            next.setDate(next.getDate() + patch.maintenance_interval_days);
            patch.next_maintenance_due = next.toISOString().slice(0, 10);
        }

        // Auto-fill org_id from session context (null until resolved)
        if (!org_id) {
            setValidationError('Organization context not loaded yet — try again in a moment.');
            return;
        }
        patch.org_id = org_id;

        try {
            const created = await createMut.mutateAsync(
                patch as Partial<Equipment> & {
                    equipment_name: string;
                    equipment_category: string;
                    org_id: string;
                }
            );
            navigate(`/equipment/${created.equipment_id}`, { replace: true });
        } catch (err) {
            setValidationError(
                err instanceof Error
                    ? err.message
                    : 'Could not create equipment. Please try again.'
            );
        }
    }

    return (
        <div className="min-h-full pb-12">
            {/* Header */}
            <header className="border-b border-pt-border/60 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <Link
                            to="/equipment"
                            className="text-pt-text-muted hover:text-pt-gold transition-colors"
                            title="Back to catalog"
                        >
                            <ArrowLeft size={20} />
                        </Link>
                        <PtLogo size={32} />
                        <div className="min-w-0">
                            <div className="text-base font-display font-semibold tracking-wide leading-tight">
                                Add Equipment
                            </div>
                            <div className="text-xs text-pt-text-dim leading-tight">
                                New catalog entry
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleSave}
                            disabled={createMut.isPending}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-pt-gold text-pt-darker text-sm font-semibold uppercase tracking-wide rounded-md disabled:opacity-50"
                        >
                            {createMut.isPending ? (
                                <span className="pt-spinner inline-block" />
                            ) : (
                                <Save size={14} strokeWidth={2.5} />
                            )}
                            Create
                        </button>
                        <span className="text-xs text-pt-text-muted hidden sm:inline">
                            {user?.email}
                        </span>
                        <button
                            onClick={signOut}
                            className="text-xs uppercase tracking-wide text-pt-text-muted hover:text-pt-gold"
                        >
                            Sign out
                        </button>
                    </div>
                </div>

                {/* Validation error banner */}
                {validationError && (
                    <div className="border-t border-pt-border bg-pt-dark">
                        <div className="max-w-4xl mx-auto px-6 py-2">
                            <div className="text-pt-red text-sm flex items-center gap-2">
                                <AlertTriangle size={14} />
                                {validationError}
                            </div>
                        </div>
                    </div>
                )}
            </header>

            <main className="max-w-4xl mx-auto px-6 py-6 space-y-4">
                {/* Identity — required section */}
                <FieldGroup
                    title="Identity"
                    description="Name, category, and identifying numbers (required)"
                >
                    <FieldRow label="Equipment Name *">
                        <TextInput
                            value={draft.equipment_name ?? null}
                            onChange={(v) => setField('equipment_name', v ?? '')}
                            placeholder="e.g. Sevoflurane Vaporizer"
                        />
                    </FieldRow>
                    <FieldRow label="Category *">
                        <SelectInput
                            value={draft.equipment_category ?? null}
                            onChange={(v) => setField('equipment_category', v ?? '')}
                            options={EQUIPMENT_CATEGORIES.map((c) => ({
                                value: c,
                                label: c
                            }))}
                            placeholder="Select category"
                        />
                    </FieldRow>
                    <FieldRow label="Subcategory">
                        <TextInput
                            value={draft.equipment_subcategory ?? null}
                            onChange={(v) => setField('equipment_subcategory', v)}
                            placeholder="e.g. Vaporizer, Scope, Monitor"
                        />
                    </FieldRow>
                    <FieldRow label="Manufacturer *">
                        <TextInput
                            value={draft.manufacturer ?? null}
                            onChange={(v) => setField('manufacturer', v)}
                            placeholder="At least one of Manufacturer or Model # required"
                        />
                    </FieldRow>
                    <FieldRow label="Model # *">
                        <TextInput
                            value={draft.model_number ?? null}
                            onChange={(v) => setField('model_number', v)}
                            placeholder="At least one of Manufacturer or Model # required"
                        />
                    </FieldRow>
                    <FieldRow label="Serial #">
                        <TextInput
                            value={draft.serial_number ?? null}
                            onChange={(v) => setField('serial_number', v)}
                            placeholder="Optional"
                        />
                    </FieldRow>
                    <FieldRow label="Asset Tag">
                        <TextInput
                            value={draft.asset_tag ?? null}
                            onChange={(v) => setField('asset_tag', v)}
                            placeholder="Optional"
                        />
                    </FieldRow>
                    <FieldRow label="Description" fullWidth>
                        <TextareaInput
                            value={draft.description ?? null}
                            onChange={(v) => setField('description', v)}
                            rows={2}
                            placeholder="Brief description of the equipment and its purpose"
                        />
                    </FieldRow>
                </FieldGroup>

                {/* Inventory */}
                <FieldGroup
                    title="Inventory"
                    description="Quantities and physical location"
                >
                    <FieldRow label="On Hand">
                        <NumberInput
                            value={draft.quantity_on_hand ?? null}
                            onChange={(v) => setField('quantity_on_hand', v ?? 1)}
                            min={0}
                        />
                    </FieldRow>
                    <FieldRow label="Minimum (Par)">
                        <NumberInput
                            value={draft.minimum_quantity ?? null}
                            onChange={(v) => setField('minimum_quantity', v ?? 1)}
                            min={0}
                        />
                    </FieldRow>
                    <FieldRow label="Maximum">
                        <NumberInput
                            value={draft.maximum_quantity ?? null}
                            onChange={(v) => setField('maximum_quantity', v)}
                            min={0}
                        />
                    </FieldRow>
                    <FieldRow label="Primary Storage">
                        <TextInput
                            value={draft.primary_storage_location ?? null}
                            onChange={(v) => setField('primary_storage_location', v)}
                            placeholder="e.g. OR Storage, Sterile Processing"
                        />
                    </FieldRow>
                    <FieldRow label="Current Location">
                        <TextInput
                            value={draft.current_location ?? null}
                            onChange={(v) => setField('current_location', v)}
                            placeholder="e.g. OR-1, PACU, Endoscopy 2"
                        />
                    </FieldRow>
                </FieldGroup>

                {/* Sterilization */}
                <FieldGroup
                    title="Sterilization"
                    description="Reusability and cleaning workflow"
                >
                    <FieldRow label="Reusable">
                        <CheckboxInput
                            value={draft.reusable ?? null}
                            onChange={(v) => setField('reusable', v)}
                            label="This item is reusable (default: yes)"
                        />
                    </FieldRow>
                    <FieldRow label="Sterilization Required">
                        <CheckboxInput
                            value={draft.sterilization_required ?? null}
                            onChange={(v) => setField('sterilization_required', v)}
                            label="Requires sterilization between uses"
                        />
                    </FieldRow>
                    <FieldRow label="Method">
                        <TextInput
                            value={draft.sterilization_method ?? null}
                            onChange={(v) => setField('sterilization_method', v)}
                            placeholder="e.g. Steam Autoclave, STERIS, Surface Wipe"
                        />
                    </FieldRow>
                </FieldGroup>

                {/* Maintenance */}
                <FieldGroup
                    title="Maintenance"
                    description="Service intervals (optional at creation)"
                    defaultOpen={false}
                >
                    <FieldRow label="Requires Maintenance">
                        <CheckboxInput
                            value={draft.requires_maintenance ?? null}
                            onChange={(v) => setField('requires_maintenance', v)}
                            label="Has scheduled maintenance"
                        />
                    </FieldRow>
                    <FieldRow label="Interval">
                        <NumberInput
                            value={draft.maintenance_interval_days ?? null}
                            onChange={(v) => setField('maintenance_interval_days', v)}
                            min={1}
                            suffix="days"
                        />
                    </FieldRow>
                    <FieldRow label="Last Maintenance">
                        <DateInput
                            value={draft.last_maintenance_date ?? null}
                            onChange={(v) => setField('last_maintenance_date', v)}
                        />
                    </FieldRow>
                    <FieldRow label="Notes" fullWidth>
                        <TextareaInput
                            value={draft.maintenance_notes ?? null}
                            onChange={(v) => setField('maintenance_notes', v)}
                            rows={2}
                            placeholder="Optional notes about maintenance history or requirements"
                        />
                    </FieldRow>
                </FieldGroup>

                {/* Compliance */}
                <FieldGroup
                    title="Compliance"
                    description="FDA approval and inspection schedule"
                    defaultOpen={false}
                >
                    <FieldRow label="FDA Approved">
                        <CheckboxInput
                            value={draft.fda_approved ?? null}
                            onChange={(v) => setField('fda_approved', v)}
                            label="FDA-approved device"
                        />
                    </FieldRow>
                    <FieldRow label="FDA Clearance #">
                        <TextInput
                            value={draft.fda_clearance_number ?? null}
                            onChange={(v) => setField('fda_clearance_number', v)}
                            placeholder="e.g. K123456"
                        />
                    </FieldRow>
                    <FieldRow label="Requires Inspection">
                        <CheckboxInput
                            value={draft.requires_inspection ?? null}
                            onChange={(v) => setField('requires_inspection', v)}
                            label="Has scheduled regulatory inspection"
                        />
                    </FieldRow>
                    <FieldRow label="Last Inspection">
                        <DateInput
                            value={draft.last_inspection_date ?? null}
                            onChange={(v) => setField('last_inspection_date', v)}
                        />
                    </FieldRow>
                    <FieldRow label="Inspection Due">
                        <DateInput
                            value={draft.inspection_due_date ?? null}
                            onChange={(v) => setField('inspection_due_date', v)}
                        />
                    </FieldRow>
                </FieldGroup>

                {/* Lifecycle */}
                <FieldGroup
                    title="Lifecycle"
                    description="Acquisition and warranty"
                    defaultOpen={false}
                >
                    <FieldRow label="Acquisition Date">
                        <DateInput
                            value={draft.acquisition_date ?? null}
                            onChange={(v) => setField('acquisition_date', v)}
                        />
                    </FieldRow>
                    <FieldRow label="Purchase Price">
                        <NumberInput
                            value={draft.purchase_price ?? null}
                            onChange={(v) => setField('purchase_price', v)}
                            min={0}
                            step="0.01"
                            suffix="USD"
                        />
                    </FieldRow>
                    <FieldRow label="Vendor">
                        <TextInput
                            value={draft.vendor ?? null}
                            onChange={(v) => setField('vendor', v)}
                        />
                    </FieldRow>
                    <FieldRow label="Warranty Expiration">
                        <DateInput
                            value={draft.warranty_expiration ?? null}
                            onChange={(v) => setField('warranty_expiration', v)}
                        />
                    </FieldRow>
                </FieldGroup>

                {/* Billing */}
                <FieldGroup
                    title="Billing"
                    description="Charge capture for billable equipment usage"
                    defaultOpen={false}
                >
                    <FieldRow label="Billable">
                        <CheckboxInput
                            value={draft.billable ?? null}
                            onChange={(v) => setField('billable', v)}
                            label="Bill patients for usage of this item"
                        />
                    </FieldRow>
                    <FieldRow label="Billing Code">
                        <TextInput
                            value={draft.billing_code ?? null}
                            onChange={(v) => setField('billing_code', v)}
                            placeholder="CPT or HCPCS"
                        />
                    </FieldRow>
                    <FieldRow label="Billing Price">
                        <NumberInput
                            value={draft.billing_price ?? null}
                            onChange={(v) => setField('billing_price', v)}
                            min={0}
                            step="0.01"
                            suffix="USD"
                        />
                    </FieldRow>
                </FieldGroup>

                {/* Help text at bottom */}
                <div className="text-xs text-pt-text-dim text-center pt-4">
                    Fields marked with * are required. Everything else can be filled in later via the detail page.
                </div>
            </main>
        </div>
    );
}
